import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { visitsAPI, notesAPI, travelAPI, clockAPI } from '../lib/api';
import { formatDate, formatTime, formatDuration, getStatusClass, capitalize, getCurrentLocation } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Calendar,
  MapPin,
  User,
  Clock,
  FileText,
  Car,
  Play,
  Square,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function VisitDetail() {
  const { visitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [notes, setNotes] = useState([]);
  const [travelLogs, setTravelLogs] = useState([]);
  const [clockRecords, setClockRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Notes dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState({
    notes: '',
    is_incident: false,
    follow_up_required: false
  });
  const [submittingNote, setSubmittingNote] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [visitRes, notesRes, travelRes, clockRes] = await Promise.all([
        visitsAPI.getById(visitId),
        notesAPI.getAll({ visit_id: visitId }),
        travelAPI.getAll({ visit_id: visitId }),
        clockAPI.getAll({ visit_id: visitId })
      ]);
      setVisit(visitRes.data);
      setNotes(notesRes.data);
      setTravelLogs(travelRes.data);
      setClockRecords(clockRes.data);
    } catch (err) {
      console.error('Failed to fetch visit:', err);
      toast.error('Failed to load visit details');
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getGPS = async () => {
    try {
      return await getCurrentLocation();
    } catch {
      toast.error('GPS unavailable');
      return null;
    }
  };

  const handleStartTravel = async () => {
    setActionLoading('travel');
    try {
      const gps = await getGPS();
      await travelAPI.start(visitId, gps);
      toast.success('Travel started');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start travel');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArrived = async (travelId) => {
    setActionLoading('arrived');
    try {
      const gps = await getGPS();
      await travelAPI.arrived(travelId, gps, null);
      toast.success('Arrival recorded');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record arrival');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClockOn = async () => {
    setActionLoading('clockon');
    try {
      const gps = await getGPS();
      await clockAPI.clockOn(visitId, gps);
      toast.success('Clocked on');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock on');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClockOff = async (clockId) => {
    setActionLoading('clockoff');
    try {
      const gps = await getGPS();
      await clockAPI.clockOff(clockId, gps);
      toast.success('Clocked off');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock off');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!newNote.notes.trim()) {
      toast.error('Please enter notes');
      return;
    }

    setSubmittingNote(true);
    try {
      await notesAPI.create({
        visit_id: visitId,
        ...newNote
      });
      toast.success('Note added');
      setNotesDialogOpen(false);
      setNewNote({ notes: '', is_incident: false, follow_up_required: false });
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const activeTravel = travelLogs.find(t => t.status === 'in_progress');
  const activeClock = clockRecords.find(c => c.status === 'clocked_in');
  const isMyVisit = visit?.staff_id === user?.id || visit?.worker_id === user?.id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="p-4">
        <p>Visit not found</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="visit-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-xl font-bold text-foreground">{visit.client_name}</h1>
          <p className="text-sm text-muted-foreground">{visit.service_type}</p>
        </div>
        <Badge className={getStatusClass(visit.status)}>{capitalize(visit.status)}</Badge>
      </div>

      {/* Visit Info */}
      <Card data-testid="visit-info-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-secondary" />
            <div>
              <p className="font-medium">{formatDate(visit.date)}</p>
              <p className="text-sm text-muted-foreground">
                {formatTime(visit.start_time)} - {formatTime(visit.end_time)}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-secondary mt-0.5" />
            <p className="text-sm">{visit.address}</p>
          </div>
          {visit.staff_name && (
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-secondary" />
              <p className="text-sm">Assigned to: {visit.staff_name}</p>
            </div>
          )}
          {visit.notes && (
            <div className="bg-muted rounded-lg p-3 mt-2">
              <p className="text-sm text-muted-foreground">{visit.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions for worker */}
      {isMyVisit && visit.status !== 'completed' && (
        <Card data-testid="actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!activeClock && (
              <div className="grid grid-cols-2 gap-3">
                {!activeTravel ? (
                  <Button
                    onClick={handleStartTravel}
                    disabled={actionLoading === 'travel'}
                    className="h-12 bg-blue-600 hover:bg-blue-700"
                    data-testid="start-travel-btn"
                  >
                    {actionLoading === 'travel' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Car className="w-5 h-5 mr-2" />
                        Start Travel
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleArrived(activeTravel.id)}
                    disabled={actionLoading === 'arrived'}
                    className="h-12 bg-amber-500 hover:bg-amber-600"
                    data-testid="arrived-btn"
                  >
                    {actionLoading === 'arrived' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <MapPin className="w-5 h-5 mr-2" />
                        Arrived
                      </>
                    )}
                  </Button>
                )}
                
                {!activeTravel && (
                  <Button
                    onClick={handleClockOn}
                    disabled={actionLoading === 'clockon'}
                    className="h-12 bg-secondary hover:bg-secondary/90"
                    data-testid="clock-on-btn"
                  >
                    {actionLoading === 'clockon' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Clock On
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {activeClock && (
              <div className="space-y-3">
                <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 rounded-full bg-secondary animate-pulse" />
                    <span className="font-semibold text-secondary">On Shift</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Started {formatTime(activeClock.clock_on_time)}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleClockOff(activeClock.id)}
                    disabled={actionLoading === 'clockoff'}
                    className="h-12 bg-red-600 hover:bg-red-700"
                    data-testid="clock-off-btn"
                  >
                    {actionLoading === 'clockoff' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Square className="w-5 h-5 mr-2" />
                        Clock Off
                      </>
                    )}
                  </Button>
                  
                  <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-12" data-testid="add-note-btn">
                        <FileText className="w-5 h-5 mr-2" />
                        Add Notes
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Visit Notes</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmitNote} className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>Notes *</Label>
                          <Textarea
                            value={newNote.notes}
                            onChange={(e) => setNewNote(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Enter your notes about this visit..."
                            rows={4}
                            data-testid="note-text-input"
                          />
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="incident"
                              checked={newNote.is_incident}
                              onCheckedChange={(checked) => setNewNote(prev => ({ ...prev, is_incident: checked }))}
                              data-testid="incident-checkbox"
                            />
                            <Label htmlFor="incident" className="font-normal cursor-pointer">
                              This is an incident report
                            </Label>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="followup"
                              checked={newNote.follow_up_required}
                              onCheckedChange={(checked) => setNewNote(prev => ({ ...prev, follow_up_required: checked }))}
                              data-testid="followup-checkbox"
                            />
                            <Label htmlFor="followup" className="font-normal cursor-pointer">
                              Follow-up required
                            </Label>
                          </div>
                        </div>
                        
                        <Button type="submit" className="w-full" disabled={submittingNote} data-testid="submit-note-btn">
                          {submittingNote ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Submit Notes
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Travel Logs */}
      {travelLogs.length > 0 && (
        <Card data-testid="travel-logs-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="w-5 h-5" />
              Travel Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {travelLogs.map(log => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">
                      {log.status === 'in_progress' ? 'In Transit' : 'Completed'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Started: {formatTime(log.start_time)}
                      {log.end_time && ` • Ended: ${formatTime(log.end_time)}`}
                    </p>
                  </div>
                  {log.duration_minutes && (
                    <Badge variant="outline">{formatDuration(log.duration_minutes)}</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clock Records */}
      {clockRecords.length > 0 && (
        <Card data-testid="clock-records-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {clockRecords.map(record => (
                <div key={record.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">
                      {record.status === 'clocked_in' ? 'Currently On Shift' : 'Completed'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      In: {formatTime(record.clock_on_time)}
                      {record.clock_off_time && ` • Out: ${formatTime(record.clock_off_time)}`}
                    </p>
                  </div>
                  {record.duration_minutes && (
                    <Badge variant="outline">{formatDuration(record.duration_minutes)}</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visit Notes */}
      <Card data-testid="visit-notes-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Visit Notes
            </CardTitle>
            {isMyVisit && (
              <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="add-note-header-btn">
                    Add Note
                  </Button>
                </DialogTrigger>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {notes.length > 0 ? (
            <div className="space-y-3">
              {notes.map(note => (
                <div key={note.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    {note.is_incident && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Incident
                      </Badge>
                    )}
                    {note.follow_up_required && (
                      <Badge variant="outline" className="gap-1 border-amber-500 text-amber-600">
                        Follow-up Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm">{note.notes}</p>
                  <p className="text-xs text-muted-foreground">
                    {note.author_name} • {formatTime(note.created_at)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
